import { useCallback, useMemo } from 'react'
import { useAtom } from 'jotai'

import { BuilderComponentSettingValue } from '@makyoapp/makyo-common'

import { editSettingsAtom } from '@/lib/jotai'
import { useActiveLayerContext } from '@/context/__project__/ActiveLayerContext'
import { useActiveDevice } from '@/hooks/useActiveDevice'
import {
  formatColorAdaptiveValue,
  formatResponsiveValueByDevice,
  hasColorAdaptiveSettings,
  hasResponsiveSettings,
} from '@/shared/builder/viewport/responsive-settings'

import { useBuilderColorSchemeV2 } from './__project__/useBuilderColorSchemeV2'

/**
 * use this hook if we want to update component settings by key directly
 */
export function useUpdateCompSettingValue(settingKey: string, componentId?: string) {
  const [device] = useActiveDevice()
  const { activeComponent, activeLayerClient, activeLayer } = useActiveLayerContext()
  const activeComponentId = componentId ?? activeComponent?.id

  const isOverlay = activeLayer?.type === 'overlay' || false
  const isDetailPage = (!!activeLayer?.tableId && activeLayer?.type === 'page') || false

  const [scheme] = useBuilderColorSchemeV2()
  const mode = scheme.current

  const [prev, setShowButtonApplyAll] = useAtom(editSettingsAtom)

  return useCallback(
    async (
      cb?:
        | BuilderComponentSettingValue
        | BuilderComponentSettingValue[]
        | (() => BuilderComponentSettingValue | BuilderComponentSettingValue[])
    ) => {
      if (!activeComponentId) return
      let component = activeLayerClient?.componentsMap?.get(activeComponentId)
      if (!component) return

      const updatedValue = typeof cb === 'function' ? cb() : cb

      const currentSettingValue = component.settings?.[settingKey]

      let updatedSettingValue = updatedValue

      if (hasColorAdaptiveSettings(settingKey)) {
        updatedSettingValue = formatColorAdaptiveValue(settingKey, currentSettingValue, updatedValue, mode)
      } else if (hasResponsiveSettings(settingKey)) {
        updatedSettingValue = formatResponsiveValueByDevice(
          settingKey,
          currentSettingValue,
          updatedValue,
          device,
          isOverlay,
          isDetailPage
        )
      }

      // handle apply all button
      if (hasResponsiveSettings(settingKey) && updatedValue !== undefined) {
        setShowButtonApplyAll((prev) => {
          let newValues = []
          let prevValues = prev.updatedValues

          if (prev.device && prev.device !== device) {
            newValues = [{ settingKey, value: updatedValue }]
          } else {
            const existingIndex = prevValues.findIndex((item: any) => item.settingKey === settingKey)

            if (existingIndex >= 0) {
              newValues = [...prevValues]
              newValues[existingIndex] = {
                ...newValues[existingIndex],
                value: updatedValue,
              }
            } else {
              newValues = [...prevValues, { settingKey, value: updatedValue }]
            }
          }

          return {
            device,
            settingKey,
            updatedValues: newValues,
          }
        })
      }

      // update component settings
      component.UpdateSettings(settingKey, updatedSettingValue)
    },
    [activeComponentId, activeLayerClient?.componentsMap, device, mode, setShowButtonApplyAll, settingKey]
  )
}

/**
 * use this hook if we want to all component settings altogether
 * @todo: make sure that this hook is not used buy design-related (responsive) setings
 */
export function useUpdateCompSettings(componentId?: string) {
  const { activeComponent, activeLayerClient } = useActiveLayerContext()
  const activeComponentId = componentId ?? activeComponent?.id

  return useCallback(
    async (cb: (settings: Record<string, any>) => void) => {
      if (!activeComponentId) return
      let component = activeLayerClient?.componentsMap.get(activeComponentId)
      if (!component) return

      let updatedSettings = JSON.parse(JSON.stringify(component.settings ?? {}))
      cb(updatedSettings)
      component.UpdateAllSettings(updatedSettings)
    },
    [activeComponentId, activeLayerClient?.componentsMap]
  )
}
